import { useState } from 'react';
import { Navigate } from 'react-router-dom';
import { BarChart3, Plus, Trash2 } from 'lucide-react';
import {
  AdminPage,
  Button,
  EmptyState,
  ErrorState,
  Panel,
  Skeleton,
  StatusBadge,
  Table,
  Td,
  Th,
  Tr,
  useUndo,
} from '@/design/components';
import { usePerm } from '@/access/usePerm';
import { formatDate } from '@/lib/dates';
import { errorMessage } from '@/lib/errors';
import { useEmployeeOptions, useEmployees } from '@/features/employees/api';
import {
  audienceLabel,
  useAdminCourses,
  useAssignmentMutations,
  useAssignments,
} from './api';
import { AssignmentReport } from './AssignmentReport';

const FIELD = 'min-h-11 w-full rounded-sm border border-muted bg-surface px-3 text-body text-ink';

/**
 * שיוך לומדות וחידונים (PRD 5.6.2) והדוח שלהם (5.6.4).
 *
 * הקהל הוא כללים ולא רשימת שמות, ולכן אפשר לשלב סניף עם תפקיד:
 * "מכירות בסניף טאגור". שדה ריק הוא "לא משנה".
 */
export function AssignmentsPage() {
  const undo = useUndo();
  const canManage = usePerm('learning.manage');
  const assignments = useAssignments();
  const courses = useAdminCourses();
  const { data: options } = useEmployeeOptions();
  const { data: employees } = useEmployees();
  const { create, remove } = useAssignmentMutations();

  const [isAdding, setIsAdding] = useState(false);
  const [openReport, setOpenReport] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  if (!canManage) return <Navigate to="/admin" replace />;

  const run = (action: () => Promise<unknown>) => {
    setError(null);
    void action().catch((caught) => setError(errorMessage(caught)));
  };

  return (
    <AdminPage
      title="שיוך לומדות"
      subtitle="מי צריך לעשות מה, ועד מתי"
      actions={
        !isAdding && (
          <Button variant="primary" onClick={() => setIsAdding(true)}>
            <Plus size={20} strokeWidth={1.75} aria-hidden="true" />
            שיוך חדש
          </Button>
        )
      }
    >
      {error && (
        <p role="alert" className="m-0 text-body-sm text-bad">
          {error}
        </p>
      )}

      {isAdding && (
        <Panel title="שיוך חדש">
          <form
            className="grid gap-3"
            onSubmit={(event) => {
              event.preventDefault();
              const form = new FormData(event.currentTarget);
              const courseId = String(form.get('course') ?? '');
              if (!courseId) return;
              const days = String(form.get('new_days') ?? '').trim();
              run(async () => {
                await create.mutateAsync({
                  course_id: courseId,
                  quiz_id: null,
                  branch_id: String(form.get('branch') ?? '') || null,
                  job_title_id: String(form.get('job') ?? '') || null,
                  employee_id: String(form.get('employee') ?? '') || null,
                  only_new_employees_days: days ? Number(days) : null,
                  due_date: String(form.get('due') ?? '') || null,
                  is_mandatory: form.get('mandatory') === 'on',
                });
                setIsAdding(false);
              });
            }}
          >
            <div className="grid gap-1">
              <label htmlFor="course" className="text-body-sm text-muted">
                הלומדה
              </label>
              <select id="course" name="course" className={FIELD} required>
                <option value="">בחירת לומדה</option>
                {courses.data
                  ?.filter((course) => course.is_published)
                  .map((course) => (
                    <option key={course.id} value={course.id}>
                      {course.title}
                    </option>
                  ))}
              </select>
              <p className="m-0 text-label text-muted">
                רק לומדות מפורסמות. לומדה בטיוטה לא תופיע אצל העובדים.
              </p>
            </div>

            <fieldset className="m-0 grid gap-3 border-0 p-0 sm:grid-cols-3">
              <legend className="mb-1 p-0 text-body-sm text-muted">
                למי. שדה ריק הוא "לא משנה".
              </legend>
              <select name="branch" aria-label="סניף" className={FIELD}>
                <option value="">כל הסניפים</option>
                {options?.branches.map((branch) => (
                  <option key={branch.id} value={branch.id}>
                    {branch.name}
                  </option>
                ))}
              </select>
              <select name="job" aria-label="תפקיד" className={FIELD}>
                <option value="">כל התפקידים</option>
                {options?.jobTitles.map((job) => (
                  <option key={job.id} value={job.id}>
                    {job.name}
                  </option>
                ))}
              </select>
              <select name="employee" aria-label="עובד/ת מסוים/ת" className={FIELD}>
                <option value="">כל העובדים</option>
                {employees?.filter((e) => e.is_active).map((employee) => (
                  <option key={employee.id} value={employee.id}>
                    {employee.first_name} {employee.last_name}
                  </option>
                ))}
              </select>
            </fieldset>

            <div className="grid gap-3 sm:grid-cols-2">
              <div className="grid gap-1">
                <label htmlFor="new_days" className="text-body-sm text-muted">
                  רק מי שהתחיל לאחרונה
                </label>
                <input
                  id="new_days"
                  name="new_days"
                  type="number"
                  min={1}
                  placeholder="מספר ימים, או ריק"
                  className={FIELD}
                />
              </div>
              <div className="grid gap-1">
                <label htmlFor="due" className="text-body-sm text-muted">
                  להשלמה עד
                </label>
                <input id="due" name="due" type="date" className={FIELD} />
              </div>
            </div>

            <label className="flex items-center gap-2 text-body">
              <input type="checkbox" name="mandatory" defaultChecked className="size-5" />
              חובה
            </label>

            <div className="flex flex-wrap gap-2">
              <Button variant="primary" type="submit" disabled={create.isPending}>
                שיוך ושליחת התראה
              </Button>
              <Button onClick={() => setIsAdding(false)}>ביטול</Button>
            </div>
          </form>
        </Panel>
      )}

      {assignments.isPending && <Skeleton className="h-40" />}
      {assignments.isError && <ErrorState onRetry={() => void assignments.refetch()} />}

      {assignments.data?.length === 0 && !isAdding && (
        <EmptyState
          title="אין עדיין שיוכים"
          description="שיוך קובע מי צריך לעשות את הלומדה ועד מתי, ושולח התראה."
        />
      )}

      {assignments.data && assignments.data.length > 0 && (
        <Table>
          <thead>
            <Tr>
              <Th>לומדה</Th>
              <Th>למי</Th>
              <Th>עד מתי</Th>
              <Th>חובה</Th>
              <Th>
                <span className="sr-only">פעולות</span>
              </Th>
            </Tr>
          </thead>
          <tbody>
            {assignments.data.map((assignment) => (
              <Tr key={assignment.id}>
                <Td>{assignment.courses?.title ?? assignment.quizzes?.title ?? '—'}</Td>
                <Td>{audienceLabel(assignment)}</Td>
                <Td className="tabular">
                  {assignment.due_date ? formatDate(assignment.due_date) : 'בלי תאריך'}
                </Td>
                <Td>
                  {assignment.is_mandatory ? (
                    <StatusBadge tone="warn">חובה</StatusBadge>
                  ) : (
                    <span className="text-muted">רשות</span>
                  )}
                </Td>
                <Td>
                  <div className="flex items-center gap-1">
                    <Button
                      variant="ghost"
                      aria-label="דוח השלמה"
                      onClick={() =>
                        setOpenReport(openReport === assignment.id ? null : assignment.id)
                      }
                    >
                      <BarChart3 size={20} strokeWidth={1.75} aria-hidden="true" />
                    </Button>
                    <Button
                      variant="ghost"
                      aria-label="ביטול השיוך"
                      onClick={() =>
                        undo.remove({
                          id: assignment.id,
                          message: 'השיוך בוטל. ההתקדמות שנרשמה נשמרת.',
                          perform: () => remove.mutateAsync(assignment.id),
                        })
                      }
                    >
                      <Trash2 size={20} strokeWidth={1.75} aria-hidden="true" />
                    </Button>
                  </div>
                </Td>
              </Tr>
            ))}
          </tbody>
        </Table>
      )}

      {openReport && (
        <AssignmentReport
          assignmentId={openReport}
          quizId={
            assignments.data?.find((a) => a.id === openReport)?.quiz_id ??
            courses.data?.find(
              (c) => c.id === assignments.data?.find((a) => a.id === openReport)?.course_id,
            )?.quizzes[0]?.id ??
            null
          }
        />
      )}
    </AdminPage>
  );
}
